import { Children, isValidElement, useCallback, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

export interface CodeBlockProps {
  children: React.ReactNode;
}

/** Extract language from the `language-*` class on the inner `<code>` element. */
function extractLanguage(children: React.ReactNode): string | null {
  const child = Children.toArray(children)[0];
  if (isValidElement(child) && typeof child.props === 'object' && child.props !== null) {
    const className = (child.props as Record<string, unknown>).className;
    if (typeof className === 'string') {
      const match = className.match(/language-(\w+)/);
      if (match) return match[1] ?? null;
    }
  }
  return null;
}

export function CodeBlock({ children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lang = extractLanguage(children);

  const handleCopy = useCallback(() => {
    const code = preRef.current?.querySelector('code');
    const text = code?.textContent ?? '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <div className="group/codeblock relative my-2">
      {/* Language label + copy button bar */}
      <div className="flex items-center justify-between rounded-t-sd-sm bg-sd-surface-elevated border border-b-0 border-sd-border/30 px-3 py-1">
        <span className="text-[10px] font-medium uppercase tracking-wider text-sd-text-muted/60 select-none">
          {lang ?? 'code'}
        </span>
        <button
          type="button"
          aria-label={copied ? 'Copied' : 'Copy code'}
          onClick={handleCopy}
          className={[
            'flex items-center gap-1 px-1.5 py-0.5 rounded-sd-sm text-sd-xs',
            'transition-[color,background-color] duration-150',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sd-primary/50',
            copied
              ? 'text-sd-success'
              : 'text-sd-text-muted hover:text-sd-warning hover:bg-sd-warning/10',
          ].join(' ')}
        >
          {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <pre
        ref={preRef}
        className="bg-sd-background/50 rounded-b-sd-sm p-3 overflow-x-auto border border-sd-border/30"
      >
        {children}
      </pre>
    </div>
  );
}
